<template>
    <div class="flex items-center gap-2">
        <!-- Checkbox Input -->
        <div v-if="config.type === 'checkbox'" class="flex items-center gap-2">
            <Checkbox
                :id="instanceId"
                :checked="modelValue"
                :disabled="isDisabled"
                @update:modelValue="$emit('change', $event)" />
            <Label :for="instanceId" class="text-sm font-normal cursor-pointer flex items-center gap-1.5">
                {{ config.label }}
                <TooltipProvider v-if="config.tooltip" :delay-duration="200">
                    <Tooltip>
                        <TooltipTrigger as-child>
                            <Info class="h-3.5 w-3.5 text-muted-foreground hover:text-foreground transition-colors cursor-help" />
                        </TooltipTrigger>
                        <TooltipContent side="top" :side-offset="4" class="max-w-xs">
                            <p class="text-sm">{{ config.tooltip }}</p>
                        </TooltipContent>
                    </Tooltip>
                </TooltipProvider>
            </Label>
        </div>

        <!-- Text/Number Input -->
        <div v-else-if="config.type === 'text' || config.type === 'number'" class="flex flex-col gap-1 flex-1">
            <Label :for="instanceId" class="text-sm font-normal flex items-center gap-1.5">
                {{ config.label }}
                <TooltipProvider v-if="config.tooltip" :delay-duration="200">
                    <Tooltip>
                        <TooltipTrigger as-child>
                            <Info class="h-3.5 w-3.5 text-muted-foreground hover:text-foreground transition-colors cursor-help" />
                        </TooltipTrigger>
                        <TooltipContent side="top" :side-offset="4" class="max-w-xs">
                            <p class="text-sm">{{ config.tooltip }}</p>
                        </TooltipContent>
                    </Tooltip>
                </TooltipProvider>
            </Label>
            <Input
                :id="instanceId"
                :type="config.type"
                :model-value="modelValue"
                :placeholder="config.placeholder"
                :disabled="isDisabled"
                @update:model-value="$emit('change', $event)"
                class="h-8" />
        </div>

        <!-- Select Input -->
        <div v-else-if="config.type === 'select'" class="flex flex-col gap-1 flex-1">
            <Label :for="instanceId" class="text-sm font-normal flex items-center gap-1.5">
                {{ config.label }}
                <TooltipProvider v-if="config.tooltip" :delay-duration="200">
                    <Tooltip>
                        <TooltipTrigger as-child>
                            <Info class="h-3.5 w-3.5 text-muted-foreground hover:text-foreground transition-colors cursor-help" />
                        </TooltipTrigger>
                        <TooltipContent side="top" :side-offset="4" class="max-w-xs">
                            <p class="text-sm">{{ config.tooltip }}</p>
                        </TooltipContent>
                    </Tooltip>
                </TooltipProvider>
            </Label>
            <Select :model-value="modelValue" :disabled="isDisabled" @update:model-value="$emit('change', $event)">
                <SelectTrigger :id="instanceId" class="h-8">
                    <SelectValue :placeholder="config.placeholder || 'Select...'" />
                </SelectTrigger>
                <SelectContent>
                    <SelectItem v-for="option in config.options" :key="option.value" :value="option.value">
                        {{ option.label }}
                    </SelectItem>
                </SelectContent>
            </Select>
        </div>
    </div>
</template>

<script setup>
import { computed } from 'vue';
import { Info } from 'lucide-vue-next';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

const props = defineProps({
    config: {
        type: Object,
        required: true
    },
    modelValue: {
        type: [String, Number, Boolean],
        default: null
    },
    instanceId: {
        type: String,
        required: true
    }
});

defineEmits(['change']);

const isDisabled = computed(() => {
    if (typeof props.config.disabled === 'function') {
        return props.config.disabled();
    }
    return props.config.disabled || false;
});
</script>
